import { createContext, Dispatch, useContext, useReducer } from 'react'

type UserCredit = {
  totalDiscountAmt: number
  fishingCoin: number
  accumulatedPoint: number
  cartCoupons: any[]
  productCoupons: any[]
}

export interface CartGroupState {
  isInvalid: boolean
  userProfile: any
  userCredit: UserCredit
  categories: any
  totalCartPriceInfo: any
  orderProductMap: Record<string, any>
  orderVariationMap: Record<string, any>
  invalidOrderVariationMap: Record<string, any>
  selectOrderVariationMap: Record<string, any>
}

export const TOGGLE_INVALID = 'TOGGLE_INVALID'
export const SET_USER_PROFILE = 'SET_USER_PROFILE'
export const SET_USER_CREDIT = 'SET_USER_CREDIT'
export const SET_CATEGORIES = 'SET_CATEGORIES'
export const SET_TOTAL_CART_PRICE_INFO = 'SET_TOTAL_CART_PRICE_INFO'
export const SET_ORDER_PRODUCT_MAP = 'SET_ORDER_PRODUCT_MAP'
export const SET_ORDER_VARIATION_MAP = 'SET_ORDER_VARIATION_MAP'
export const SET_INVALID_ORDER_VARIATION_MAP = 'SET_INVALID_ORDER_VARIATION_MAP'
export const SET_SELECT_ORDER_VARIATION_MAP = 'SET_SELECT_ORDER_VARIATION_MAP'
export const CLEAR_USER_CREDIT = 'CLEAT_USER_CREDIT'
export const CLEAR_TOTAL_CART_PRICE_INFO = 'CLEAR_TOTAL_CART_PRICE_INFO'
export const CLEAR_ORDER_CART_MAPS = 'CLEAR_ORDER_CART_MAPS'
export const CLEAR_CART_GROUP_STATE = 'CLEAR_CART_GROUP_STATE'

export type CartGroupAction =
  | { type: typeof TOGGLE_INVALID; payload: boolean }
  | { type: typeof SET_USER_PROFILE; payload: any }
  | { type: typeof SET_USER_CREDIT; payload: UserCredit }
  | { type: typeof SET_CATEGORIES; payload: any }
  | { type: typeof SET_TOTAL_CART_PRICE_INFO; payload: any }
  | { type: typeof SET_ORDER_PRODUCT_MAP; payload: Record<number, any> }
  | { type: typeof SET_ORDER_VARIATION_MAP; payload: Record<number, any> }
  | { type: typeof SET_INVALID_ORDER_VARIATION_MAP; payload: Record<number, any> }
  | { type: typeof SET_SELECT_ORDER_VARIATION_MAP; payload: Record<number, any> }
  | { type: typeof CLEAR_USER_CREDIT }
  | { type: typeof CLEAR_TOTAL_CART_PRICE_INFO }
  | { type: typeof CLEAR_ORDER_CART_MAPS }
  | { type: typeof CLEAR_CART_GROUP_STATE }

const initialState: CartGroupState = {
  isInvalid: false,
  userProfile: undefined,
  userCredit: {
    totalDiscountAmt: 0,
    cartCoupons: [],
    productCoupons: [],
    fishingCoin: 0,
    accumulatedPoint: 0,
  },
  categories: undefined,
  totalCartPriceInfo: undefined,
  orderProductMap: {},
  orderVariationMap: {},
  invalidOrderVariationMap: {},
  selectOrderVariationMap: {},
}

const cartGroupReducer = (state: CartGroupState, action: CartGroupAction): CartGroupState => {
  switch (action.type) {
    case TOGGLE_INVALID:
      return { ...state, isInvalid: action.payload }
    case SET_USER_PROFILE:
      return { ...state, userProfile: action.payload }
    case SET_USER_CREDIT:
      return { ...state, userCredit: action.payload }
    case SET_CATEGORIES:
      return { ...state, categories: action.payload }
    case SET_TOTAL_CART_PRICE_INFO:
      return { ...state, totalCartPriceInfo: action.payload }
    case SET_ORDER_PRODUCT_MAP:
      return { ...state, orderProductMap: action.payload }
    case SET_ORDER_VARIATION_MAP:
      return { ...state, orderVariationMap: action.payload }
    case SET_INVALID_ORDER_VARIATION_MAP:
      return { ...state, invalidOrderVariationMap: action.payload }
    case SET_SELECT_ORDER_VARIATION_MAP:
      return { ...state, selectOrderVariationMap: action.payload }
    case CLEAR_USER_CREDIT:
      return { ...state, userCredit: initialState.userCredit }
    case CLEAR_TOTAL_CART_PRICE_INFO:
      return { ...state, totalCartPriceInfo: initialState.totalCartPriceInfo }
    case CLEAR_ORDER_CART_MAPS:
      return {
        ...state,
        orderProductMap: initialState.orderProductMap,
        orderVariationMap: initialState.orderVariationMap,
        invalidOrderVariationMap: initialState.invalidOrderVariationMap,
        selectOrderVariationMap: initialState.selectOrderVariationMap,
      }
    case CLEAR_CART_GROUP_STATE:
      return {
        ...initialState,
      }
    default:
      return state
  }
}

export interface CartGroupContextType {
  state: CartGroupState
  dispatch: Dispatch<CartGroupAction>
}

const CartGroupContext = createContext<CartGroupContextType | undefined>(undefined)

export const CartGroupProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(cartGroupReducer, initialState)

  const value = {
    state,
    dispatch,
  }

  return <CartGroupContext.Provider value={value}>{children}</CartGroupContext.Provider>
}

export const useCartGroupContext = () => {
  const context = useContext(CartGroupContext)
  if (!context) {
    throw new Error('useCartGroupContext must be used within a CartGroupProvider')
  }
  return context
}
